import { createContext, useContext } from 'react';
import type { Vendor, Position, Category, Filters, PositionNames } from '../types';
import type { FetchErrorKind } from '../data';

export type MarketDataErrorKind = FetchErrorKind;

export interface MarketDataState {
  vendors: Vendor[];
  positions: Position[];
  categories: Category[];
  names: PositionNames;
  filters: Filters;
  positionWarnings: string[];
}

export interface MarketDataContextValue {
  state: MarketDataState;
  /** UIDs of positions deleted in the current editor session. Used by the
   *  CSV serializer to guard against stale-closure issues. */
  deletedPositionUids: ReadonlySet<string>;
  setSearch: (search: string) => void;
  showAllCategories: () => void;
  setCategoryFilter: (ids: Set<string>) => void;
  movePosition: (uid: string, coordinates: [number, number]) => void;
  movePositionsBulk: (updates: ReadonlyMap<string, [number, number]>) => void;
  addPosition: (position: Position) => void;
  updatePosition: (uid: string, patch: Partial<Pick<Position, 'type' | 'labelPosition' | 'name' | 'description'>>) => void;
  renamePosition: (uid: string, newId: string) => void;
  deletePosition: (uid: string) => void;
}

export const MarketDataContext = createContext<MarketDataContextValue | null>(null);

export function useMarketDataContext(): MarketDataContextValue {
  const ctx = useContext(MarketDataContext);
  if (!ctx) throw new Error('useMarketDataContext must be used within MarketDataProvider');
  return ctx;
}
